import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { RegisterHelperService } from '@modules/register/services/register-helper.service';
import { SecureUtils } from '@utils/secure-utils';
import { throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ForgetOperation } from '../../models/operation-register';
import { ForgotPasswordOrigin } from '../../models/user';
import { createHttpParams } from '../../utils/utils';
import { BaseService } from '../core/base.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class ForgotPasswordService {
  blockedUsersInThisSession = new Set<string>();

  constructor(
    private base: BaseService,
    private http: HttpClient,
    private or: OperationRegisterService,
    private mmobileHelper: MMobileHelperService,
    private registerHelperService: RegisterHelperService,
  ) { }

  getUser({ documentNumber, phone, email }) {
    let url = this.mmobileHelper.getCommonServiceUrl('middlewarePublicServices') + '/selfArea/client/validation';
    const params: any = {
      documentNumber,
      roEntityCode: '00',
      roGroupingCode: '0',
      roOrigin: `${this.or.getOrigin()}`
    };
    if (phone) {
      url = `${url}/phone`;
      params.phone = phone;
    }
    if (email) {
      url = `${url}/mail`;
      params.mail = email;
    }
    const headers = this.base.getPublicHeaders();

    return this.http.post<any>(url, params, { ...headers });
  }

  sendTemporalPassword({ documentNumber, phone, email }: Partial<any>) {
    let url = this.mmobileHelper.getCommonServiceUrl('middlewarePublicServices') + '/selfArea/client';
    const params: any = {
      documentNumber,
      roEntityCode: '00',
      roGroupingCode: '0',
      roOrigin: `${this.or.getOrigin()}`
    };
    if (phone) {
      url = `${url}/sendPassByPhone`;
      params.phone = phone;
    } else if (email) {
      url = `${url}/sendPassByMail`;
      params.mail = email;
    } else {
      url = `${url}/sendPassByMail`;
    }
    const headers = this.base.getPublicHeaders();
    return this.http.post<any>(url, params, { ...headers });
  }

  block({ documentNumber, birthdate, origin }) {
    const url = `${this.base.getPublicBaseUrl()}/clientsArea/login/passwordRecovery/user/block`;
    const headers = this.base.getPublicHeaders();
    const params = createHttpParams({
      documentNumber,
      birthdate
    });

    return this.http.post(url, {}, { ...headers, params }).pipe(
      tap(() => {
        const structureDevice = JSON.stringify(this.registerHelperService.getDeviceStructure(ForgetOperation.USER_NOT_FOUND));
        this.or.registerForgetPasswordOperation(
          ForgetOperation.BLOCKED_USER,
          origin || ForgotPasswordOrigin.FROM_LOGIN,
          documentNumber,
          structureDevice
        );
      })
    );
  }

  updatePassword({ documentNumber, newPassword, origin, currentPassword }) {
    const url = this.mmobileHelper.getCommonServiceUrl('middlewarePublicServices') + '/selfArea/client/token/password';
    const headers = this.base.getPublicHeaders();
    const body = {
      documentNumber,
      newPass: newPassword,
      token: SecureUtils.getHashFrom('MEETINGMETS' + currentPassword.toUpperCase())
    };
    const params = this.or.getForgotPasswordROQueryParams({
      documentNumber,
      origin
    });
    return this.http.put(url, body, { ...headers, params }).pipe(
      tap(() => { }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  getSecurityQuestions() {
    const url = `${this.base.getPublicBaseUrl()}/clientsArea/login/passwordRecovery/questions`;
    const headers = this.base.getPublicHeaders();

    return this.http
      .get<{ code: number; question: string; }[]>(url, headers)
      .pipe(map(q => q.filter(({ code }) => code !== -1)));
  }

  /* BUSINESSES */

  sendPasswordRecoveryEmail(cif: string, nif: string, email: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/passRecovery/validate/user/email`;
    const requestBody = { cif, nif, email };
    return this.http.post(url, requestBody);
  }

  sendPasswordRecoverySMS(cif: string, nif: string, phone: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/passRecovery/validate/user/sms`;
    const requestBody = { cif, nif, phone };
    return this.http.post<{ token: string; }>(url, requestBody);
  }

  validatePasswordRecoveryCode(code: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/passRecovery/validate/code`;
    const requestBody = { code };
    return this.http.post<{ token: string; }>(url, requestBody);
  }

  validatePasswordRecoveryPin(pin: string, token: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/passRecovery/validate/pin`;
    const requestBody = { pin };
    const headers = new HttpHeaders()
      .append('Authorization', token);
    return this.http.post<{ token: string; }>(url, requestBody, { headers });
  }

  recoverPassword(token: string, password: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/passRecovery/recover`;
    const requestBody = { password };
    const headers = new HttpHeaders()
      .append('Authorization', token);
    return this.http.post(url, requestBody, { headers });
  }
}
